<template>
    <div class="order-body" :class="{ 'multicart-mode': mode === 'multicart', 'packlist-mode': mode === 'packlist' }">
        <!-- Upsell section for both modes -->
        <UpsellItems
            :mode="mode"
            :upsellItems="upsellItems"
            :currentFlyer="currentFlyer"
            :packaging="packaging"
        />
        
        <!-- Articles container -->
        <div class="articles-container" :class="{ 'linear-layout': layout === 'linear', 'grid-layout': layout === 'grid' }">
            <ArticleCard
                v-for="article in articles"
                :key="article.id"
                :article="article"
                :packlistItems="packlistItems"
                :isDarkBackground="mode === 'packlist' ? localDarkBackground : isDarkBackground"
                :mode="mode"
                :nextItemToScan="nextItemToScan"
                :isPartOfPacklistChecker="isPartOfPacklistChecker"
                :isInDifferentPacklistChecker="isInDifferentPacklistChecker"
                :layout="layout"
                :mobileWorkflowStep="mobileWorkflowStep"
                :pickingScannedArticles="pickingScannedArticles"
                :packingScannedArticles="packingScannedArticles"
                @reset-article="resetArticle"
                @copy-to-clipboard="copyToClipboard"
                @toggle-background-mode="toggleGlobalBackgroundMode"
            />
        </div>
    </div>
</template>

<script>
import ArticleCard from './ArticleCard.vue';
import UpsellItems from './UpsellItems.vue';

export default {
    name: 'OrderBody',
    components: {
        ArticleCard,
        UpsellItems
    },
    props: {
        // Common props
        articles: {
            type: Array,
            default: () => []
        },

        // Multicart-specific props
        upsellItems: {
            type: Array,
            default: () => []
        },
        currentFlyer: {
            type: Object,
            default: null
        },
        packaging: {
            type: Object,
            default: null
        },
        
        // Packlist-specific props
        packlistItems: {
            type: Array,
            default: () => []
        },
        isDarkBackground: {
            type: Boolean,
            default: false
        },
        isDesktop: {
            type: Boolean,
            default: false
        },
        productsCount: {
            type: Number,
            default: 0
        },
        currentOrderId: {
            type: String,
            default: null
        },
        warrantyReason: {
            type: String,
            default: ''
        },
        
        // Status props (shared)
        hasMissingItems: {
            type: Boolean,
            default: false
        },
        isMulticart: {
            type: Boolean,
            default: false
        },
        isWarranty: {
            type: Boolean,
            default: false
        },
        
        // Mode selector
        mode: {
            type: String,
            default: 'multicart', // 'packlist' or 'multicart'
            validator: value => ['packlist', 'multicart'].includes(value)
        },
        nextItemToScan: {
            type: Object,
            default: null
        },
        isPartOfPacklistChecker: {
            type: Function,
            default: () => () => true
        },
        isInDifferentPacklistChecker: {
            type: Function,
            default: () => () => false
        },

        // Mobile workflow state props (for desktop mirroring)
        mobileWorkflowStep: {
            type: Number,
            default: 1
        },
        pickingScannedArticles: {
            type: Object,
            default: () => ({})
        },
        packingScannedArticles: {
            type: Object,
            default: () => ({})
        },
        layout: {
            type: String,
            default: 'grid',
            validator: value => ['grid', 'linear'].includes(value)
        }
    },
    data() {
        return {
            localDarkBackground: false
        }
    },
    computed: {
    },
    watch: {
        articles: {
            handler(newVal, oldVal) {
                if (this.mode === 'packlist') {
                    console.log('OrderBody - articles prop changed:', newVal);
                    console.log('OrderBody - articles count:', newVal.length);
                }
            },
            deep: true
        },
    },
    methods: {
        resetArticle(article) {
            this.$emit('reset-article', article);
        },
        copyToClipboard(text) {
            this.$emit('copy-to-clipboard', text);
        },
        toggleGlobalBackgroundMode() {
            if (this.mode === 'packlist') {
                this.localDarkBackground = !this.localDarkBackground;

                let backgroundSettings = {
                    isDarkBackground: this.localDarkBackground
                }

                localStorage.setItem('backgroundSettings', JSON.stringify(backgroundSettings));
            }
            this.$emit('toggle-background-mode');
        },
        loadBackgroundSettings() {
            let backgroundSettings = localStorage.getItem('backgroundSettings');

            if (!backgroundSettings) {
                let newBackgroundSettings = {
                    isDarkBackground: false
                }

                localStorage.setItem('backgroundSettings', JSON.stringify(newBackgroundSettings));
                return false;
            } else {
                return JSON.parse(backgroundSettings).isDarkBackground;
            }
        }
    },
    mounted() {
        if (this.mode === 'packlist') {
            // Load background settings for packlist mode
            this.localDarkBackground = this.loadBackgroundSettings();
        }
    },
    updated() {
        if (this.mode === 'packlist') {
            // Component updated - avoid infinite loops, let computed properties handle reactivity
            console.log('🔄 OrderBody (packlist mode) updated');
        }
    }
}
</script>

<style scoped>
/* Unified styling for both modes */
.order-body.multicart-mode,
.order-body.packlist-mode {
    flex: 1;
    border-left: 1px solid #e0e0e0;
    height: 100%;
    min-height: 0; /* Important for flex shrinking */
    overflow-y: auto;
    background-color: #f8f9fa;
    box-sizing: border-box;
}

.multicart-mode .articles-container,
.packlist-mode .articles-container {
    padding: 16px;
}

/* Grid Layout - Max 3 columns, responsive to article count */
.articles-container.grid-layout {
    display: grid;
    gap: 16px;
}

/* Default: up to 3 columns */
.articles-container.grid-layout {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    max-width: 100%;
}

/* Force max 3 columns on large screens */
@media (min-width: 1200px) {
    .articles-container.grid-layout {
        grid-template-columns: repeat(auto-fit, minmax(max(280px, calc((100% - 32px) / 3)), 1fr));
    }
}

/* Linear Layout - Single column list */
.articles-container.linear-layout {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

/* Tablet responsive adjustments (768px - 1366px) */
@media (min-width: 768px) and (max-width: 1366px) {
    .multicart-mode .articles-container,
    .packlist-mode .articles-container {
        padding: 12px;
    }

    .articles-container.grid-layout {
        gap: 12px;
    }

    .articles-container.linear-layout {
        gap: 10px;
    }
}

/* Mobile responsive adjustments */
@media (max-width: 767px) {
    .multicart-mode,
    .packlist-mode {
        padding: 12px;
    }

    .articles-container.grid-layout {
        grid-template-columns: 1fr; /* Single column on mobile */
        gap: 12px;
    }

    .articles-container.linear-layout {
        gap: 12px;
    }

    .status-badge {
        right: 16px;
        padding: 10px 12px;
        min-width: 100px;
    }

    .badge-count {
        font-size: 16px;
    }

    .badge-label {
        font-size: 10px;
    }
}
</style>